/**
 * Shared Zod schemas for the Recurring Trips mutating endpoints.
 *
 * `recurringTripBodySchema` is the create/edit contract for
 * `POST /api/recurring-trips` and `PATCH /api/recurring-trips/:id` (routes land
 * in the Phase-2 logic pass). It reuses the exact ride snapshot fields from
 * `rideBodySchema` (waypoints / paymentOption / extraCategories / passenger)
 * so a generated ride is a straight copy, plus the recurrence block.
 *
 * As with `rideBodySchema`, the schema is intentionally permissive on the
 * reused order fields (the route does its own per-field fallbacks); the
 * recurrence fields carry the real invariants (at least one weekday, a valid
 * "HH:mm" pickup time, end >= start — the last enforced in the route/UI).
 */
import { z } from 'zod';
import {
  RECURRING_TRIP_STATUSES,
  RECURRING_TRIP_EXCEPTION_TYPES,
} from '../../constants/index.js';
import { orderSnapshotRequestShape } from '../responses/rides.js';

/** "HH:mm" 24-hour time-of-day, e.g. "08:00" / "13:30". */
const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'recurringTrips.errors.invalidTime');
/** "YYYY-MM-DD" calendar date. */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'recurringTrips.errors.invalidDate');

/**
 * One active weekday's schedule: its own pickup time and optional return leg.
 * `returnTime` is required (valid "HH:mm") when `returnTrip` is true, otherwise
 * it may be null/omitted. The route + materializer read the same shape.
 */
const daySchedule = z.object({
  weekDay:    z.number().int().min(1).max(7),
  pickupTime: timeOfDay,
  returnTrip: z.boolean().optional().default(false),
  returnTime: timeOfDay.nullable().optional(),
}).refine(d => !d.returnTrip || !!d.returnTime, {
  message: 'recurringTrips.errors.invalidTime', path: ['returnTime'],
});

/**
 * Body schema for adding an exception date to a recurring template
 * (`POST /api/recurring-trips/:id/exceptions`).
 */
export const recurringTripExceptionInputSchema = z.object({
  date: isoDate,
  type: z.enum(
    Object.values(RECURRING_TRIP_EXCEPTION_TYPES) as [string, ...string[]],
    { message: 'recurringTrips.errors.exceptionTypeRequired' },
  ),
  reason: z.string().nullable().optional(),
});

export type RecurringTripExceptionInput = z.infer<typeof recurringTripExceptionInputSchema>;

/**
 * Body shape for `POST /api/recurring-trips` and `PATCH /api/recurring-trips/:id`.
 * The reused order fields match `rideBodySchema` field-for-field so both forms
 * stay in lock-step; the recurrence block below is the template-only surface.
 */
export const recurringTripBodySchema = z.object({
  // Dispatcher-facing template name (required).
  name:                  z.string().trim().min(1, 'recurringTrips.errors.nameRequired'),
  // ── Reused order fields (shared with rideBodySchema via
  //    `orderSnapshotRequestShape`) ──
  ...orderSnapshotRequestShape,
  // Template-only order field (not part of the shared ride snapshot).
  organizationId:        z.string().nullable().optional(),

  // ── Recurrence ──
  // Per-weekday schedule — at least one active weekday, each with its own
  // pickup + optional return time.
  daySchedules:       z.array(daySchedule).min(1, 'recurringTrips.errors.weekdayRequired'),
  startDate:          isoDate,
  endDate:            isoDate.nullable().optional(),
  repeatEveryWeeks:   z.number().int().min(1).max(3).optional(),
  skipPublicHolidays: z.boolean().optional(),
  skipSchoolHolidays: z.boolean().optional(),

  // ── Lifecycle (optional on create; PATCHed by the pause/resume/cancel routes) ──
  status:      z.enum(Object.values(RECURRING_TRIP_STATUSES) as [string, ...string[]]).optional(),
  pauseReason: z.string().nullable().optional(),
});

export type RecurringTripBodyInput = z.infer<typeof recurringTripBodySchema>;
